import {
    Alert, Box, Grid, Paper, Snackbar, Stack, Typography,
    TextField, MenuItem, FormControlLabel, Checkbox, List, ListItem,
    IconButton, Button, FormLabel, CircularProgress, LinearProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import axios from 'axios';
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEndpoints } from '../utils/EndpointContext';
import { defaultValues } from '../models/defaultValues';
import { FileModel } from '../models/FileModel';
import TextInput from '../components/forms/TextInput';
import SaveAndDeleteButtons from '../components/forms/SaveAndDeleteButtons';
import { PlaceholderType, PlaceholderTypeOptions, PlaceholderValues } from '../models/PlaceholderValues';
import ConfirmDeleteModal from '../components/modals/ConfirmDeleteModal';
import React from 'react';
import { EditableContext } from '../contexts/EditableContext';

type Params = {
    companyCode: string;
    productType: string;
    versionId: string;
    key: string;
};

const asArray = (value: string | string[] | undefined): string[] =>
    Array.isArray(value) ? value : (typeof value === 'string' ? [value] : []);

type State = {
    model: PlaceholderValues;
    dpDraft: string;
    dpError?: string;
    keyDraft: string;
    keyError?: string;
    newOption: string;
    hasUnsavedChanges: boolean;
};

type Action =
    | { type: 'HYDRATE'; model: PlaceholderValues }
    | { type: 'TYPE_SET'; value: PlaceholderType }
    | { type: 'LABEL_SET'; value: string }
    | { type: 'NULLABLE_SET'; value: boolean }
    | { type: 'DP_DRAFT_SET'; value: string }
    | { type: 'DP_COMMIT' }
    | { type: 'KEY_DRAFT_SET'; value: string }
    | { type: 'KEY_COMMIT'; otherKeys: Set<string> }
    | { type: 'NEW_OPTION_SET'; value: string }
    | { type: 'OPTION_ADD' }
    | { type: 'OPTION_UPDATE'; index: number; value: string }
    | { type: 'OPTION_REMOVE'; index: number }
    | { type: 'OPTION_MOVE'; from: number; to: number }
    | { type: 'MARK_SAVED' };

function Reducer(state: State, action: Action): State {
    switch (action.type) {
        case 'HYDRATE': {
            const model = action.model;
            return {
                ...state,
                model: model,
                dpDraft: model.DecimalPlaces != null ? String(model.DecimalPlaces) : '',
                dpError: undefined,
                keyDraft: model.Key ?? '',
                keyError: undefined,
                newOption: '',
                hasUnsavedChanges: false,
            }
        }

        case 'TYPE_SET': {
            const next = { ...state.model, Type: action.value };
            if (action.value !== PlaceholderType.list) next.Options = [];
            if (action.value !== PlaceholderType.float) next.DecimalPlaces = undefined;
            return {
                ...state,
                model: next,
                dpDraft: action.value === PlaceholderType.float && state.model.DecimalPlaces != null
                    ? String(state.model.DecimalPlaces) : '',
                dpError: undefined,
                hasUnsavedChanges: true,
            }
        }

        case 'LABEL_SET':
            return { ...state, model: { ...state.model, Label: action.value }, hasUnsavedChanges: true };

        case 'NULLABLE_SET':
            return { ...state, model: { ...state.model, Nullable: action.value }, hasUnsavedChanges: true };

        case 'DP_DRAFT_SET':
            return { ...state, dpDraft: action.value, dpError: undefined };

        case 'DP_COMMIT': {
            const rawValue = state.dpDraft.trim();
            const numValue = Number(rawValue);
            if (!rawValue) return { ...state, dpError: 'Must have a value' };
            if (!Number.isInteger(numValue) || numValue < 1) return { ...state, dpError: 'Enter a whole number ≥ 1' };
            return {
                ...state,
                model: { ...state.model, DecimalPlaces: numValue },
                dpDraft: String(numValue),
                dpError: undefined,
                hasUnsavedChanges: true,
            };
        }

        case 'KEY_DRAFT_SET':
            return { ...state, keyDraft: action.value, keyError: undefined };

        case 'KEY_COMMIT': {
            const key = state.keyDraft.trim();
            if (!key) return { ...state, keyError: 'Key cannot be empty' };
            if (action.otherKeys.has(key)) return { ...state, keyError: 'Key already exists' };
            return { ...state, model: { ...state.model, Key: key }, keyError: undefined, hasUnsavedChanges: true };
        }

        case 'NEW_OPTION_SET':
            return { ...state, newOption: action.value };

        case 'OPTION_ADD': {
            const val = state.newOption.trim();
            if (!val) return state;
            const opts = [...(state.model.Options ?? []), val];
            return { ...state, model: { ...state.model, Options: opts }, newOption: '', hasUnsavedChanges: true };
        }
        case 'OPTION_UPDATE': {
            const opts = (state.model.Options ?? []).map((value, index) => (index === action.index ? action.value : value));
            return { ...state, model: { ...state.model, Options: opts }, hasUnsavedChanges: true };
        }
        case 'OPTION_REMOVE': {
            const opts = (state.model.Options ?? []).filter((_, index) => index !== action.index);
            return { ...state, model: { ...state.model, Options: opts }, hasUnsavedChanges: true };
        }
        case 'OPTION_MOVE': {
            const opts = [...(state.model.Options ?? [])];
            if (action.to < 0 || action.to >= opts.length || action.from === action.to) return state;
            const [removed] = opts.splice(action.from, 1);
            opts.splice(action.to, 0, removed);
            return { ...state, model: { ...state.model, Options: opts }, hasUnsavedChanges: true };
        }

        case 'MARK_SAVED':
            return { ...state, hasUnsavedChanges: false };

        default:
            return state;
    }
}

const PlaceholderPage: React.FC = () => {
    const { companyCode, versionId, productType, key } = useParams<Params>();
    const endPoints = useEndpoints();
    const navigate = useNavigate();

    const itemsEndRef = useRef<null | HTMLDivElement>(null);

    const [openAlert, setOpenAlert] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [savedData, setSavedData] = useState<FileModel>();
    const [placeholders, setPlaceholders] = useState<PlaceholderValues[]>([]);
    const [indexInFile, setIndexInFile] = useState<number>(-1);

    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const [state, dispatch] = useReducer(Reducer, {
        model: defaultValues.Placeholder,
        dpDraft: '',
        keyDraft: '',
        newOption: '',
        hasUnsavedChanges: false,
    } as State);

    const otherKeys = useMemo(
        () =>
            new Set(
                (placeholders ?? [])
                    .filter((_, index) => index !== indexInFile)
                    .map(placeholder => placeholder.Key ?? '')
                    .filter(Boolean)
            ),
        [placeholders, indexInFile]
    );

    const opts = useMemo(() => asArray(state.model.Options), [state.model.Options]);
    const trimmedKey = useMemo(() => state.keyDraft.trim(), [state.keyDraft]);
    const isKeyValid = trimmedKey.length > 0 && !otherKeys.has(trimmedKey);
    const isEditable = !savedData?.DatePublished;

    useEffect(() => {
        axios.get(`${endPoints.questionEditorRead}`, {
            params: {
                partitionKey: companyCode,
                rowKey: versionId,
                downloadData: true
            }
        }).then(res => {
            const file: FileModel = res.data;
            const list = file.Data!.Placeholders ?? [];
            const idx = list.findIndex(placeholder => (placeholder.Key ?? '') === (key ?? ''));
            const found = idx >= 0 ? list[idx] : { ...defaultValues.Placeholder, Key: key ?? '' };

            dispatch({ type: 'HYDRATE', model: found });

            setSavedData(file);
            setPlaceholders(list);
            setIndexInFile(idx);
        });
    }, [companyCode, endPoints.questionEditorRead, key, versionId]);

    useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            if (!state.hasUnsavedChanges) return;
            e.preventDefault();
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [state.hasUnsavedChanges]);

    useEffect(() => {
        setIsLoading(true);
        axios.get(endPoints.questionEditorRead, {
            params: { partitionKey: companyCode, rowKey: versionId, downloadData: true }
        })
            .then(res => {
                const file: FileModel = res.data;
                const list = file.Data!.Placeholders ?? [];
                const idx = list.findIndex(p => (p.Key ?? '') === (key ?? ''));
                const found = idx >= 0 ? list[idx] : { ...defaultValues.Placeholder, Key: key ?? '' };

                dispatch({ type: 'HYDRATE', model: found });
                setSavedData(file);
                setPlaceholders(list);
                setIndexInFile(idx);
            })
            .finally(() => setIsLoading(false));
    }, [companyCode, endPoints.questionEditorRead, key, versionId]);

    const handleAlertClose = () => setOpenAlert(false);

    const onSaveClick = async () => {
        if (!isKeyValid) {
            dispatch({ type: 'KEY_COMMIT', otherKeys });
            return;
        }
        await handleSave();
    };

    const handleSave = async () => {

        const trimmed = state.keyDraft.trim();
        const isKeyValid = trimmed.length > 0 && !otherKeys.has(trimmed);
        if (!isKeyValid) { dispatch({ type: 'KEY_COMMIT', otherKeys }); return; }

        if (isSaving) return;
        setIsSaving(true);
        try {
            if (state.model.Type === PlaceholderType.float && state.dpDraft && state.model.DecimalPlaces == null) {
                dispatch({ type: 'DP_COMMIT' });
            }

            const file = { ...savedData } as FileModel;
            const list = [...(file.Data!.Placeholders ?? [])];
            if (indexInFile >= 0) list[indexInFile] = state.model;
            else { list.push(state.model); setIndexInFile(list.length - 1); }

            file.Data!.Placeholders = list;
            setSavedData(file);
            setPlaceholders(list);

            await axios.post(`${endPoints.questionEditorUpdate}`, file);
            setOpenAlert(true);

            if ((key ?? '') !== (state.model.Key ?? '')) {
                navigate(`/${companyCode}/dataversions/${productType}/data/${versionId}/placeholders/${encodeURIComponent(state.model.Key!)}`, { replace: true });
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = () => setOpenDeleteModal(true);

    const confirmDelete = async () => {
        try {
            if (indexInFile >= 0) {
                const file = { ...savedData } as FileModel;
                const list = [...(file.Data!.Placeholders ?? [])];
                list.splice(indexInFile, 1);
                file.Data!.Placeholders = list;
                await axios.post(`${endPoints.questionEditorUpdate}`, file);
            }
            navigate(-1);
        } catch (e) {
            console.log(e);
        }
    };

    const updateOptionAt = (index: number, value: string) =>
        dispatch({ type: 'OPTION_UPDATE', index: index, value });

    const removeOptionAt = (index: number) =>
        dispatch({ type: 'OPTION_REMOVE', index: index });

    const addOption = () =>
        dispatch({ type: 'OPTION_ADD' });

    const moveOption = (fromIdx: number, toIdx: number) =>
        dispatch({ type: 'OPTION_MOVE', from: fromIdx, to: toIdx });

    const handleTypeChange = (type: PlaceholderType) => {
        dispatch({ type: 'TYPE_SET', value: type });
    };

    const commitDecimalPlaces = () => {
        dispatch({ type: 'DP_COMMIT' });
    };

    const commitKey = () => dispatch({ type: 'KEY_COMMIT', otherKeys });

    return (
        <div>
            <EditableContext.Provider value={isEditable}>
                {isSaving && <LinearProgress sx={{ mb: 1 }} />}
                <Grid container spacing={2} justifyContent="flex-end" sx={{ mb: '1em' }}>
                    <Grid item xs={6}></Grid>
                    <SaveAndDeleteButtons
                        onSave={onSaveClick}
                        onDelete={handleDelete}
                        disabled={!isKeyValid || !state.hasUnsavedChanges}
                        saving={isSaving}
                    />
                </Grid>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>

                        <fieldset disabled={isSaving || isLoading} style={{ border: 0, padding: 0, margin: 0 }}>
                            <Stack spacing={2}>
                                <Typography>Placeholder</Typography>
                                <Paper>
                                    <fieldset disabled={isSaving} style={{ border: 0, padding: 0, margin: 0 }}>
                                        <Box sx={{ m: 2 }}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={6}>
                                                    <Stack spacing={2}>
                                                        <TextField
                                                            label="Key"
                                                            value={state.keyDraft}
                                                            disabled={!isEditable}
                                                            onChange={(e) => dispatch({ type: 'KEY_DRAFT_SET', value: e.target.value })}
                                                            onBlur={commitKey}
                                                        />
                                                        {state.keyError && <span style={{ color: '#d32f2f', fontSize: 12 }}>{state.keyError}</span>}
                                                        <TextInput
                                                            fieldName="Label"
                                                            label="Label"
                                                            value={state.model.Label ?? ''}
                                                            onChange={(v) => dispatch({ type: 'LABEL_SET', value: v })}
                                                        />
                                                        <TextField select label="Type" value={state.model.Type} disabled={!isEditable}
                                                            onChange={(e) => handleTypeChange(Number(e.target.value) as PlaceholderType)}>
                                                            {PlaceholderTypeOptions.map(({ value, label }) => (
                                                                <MenuItem key={value} value={value}>{label}</MenuItem>
                                                            ))}
                                                        </TextField>

                                                        <FormControlLabel
                                                            label="Nullable"
                                                            control={
                                                                <Checkbox
                                                                    checked={!!state.model.Nullable}
                                                                    disabled={!isEditable}
                                                                    onChange={(e) => dispatch({ type: 'NULLABLE_SET', value: e.target.checked })}
                                                                />
                                                            }
                                                        />
                                                    </Stack>
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <Stack spacing={2}>
                                                        {state.model.Type === PlaceholderType.list && (
                                                            <Box
                                                                component="fieldset"
                                                                sx={{
                                                                    border: 1,
                                                                    borderColor: 'divider',
                                                                    borderRadius: 1,
                                                                    p: 2,
                                                                    pt: 3,
                                                                    mb: 1
                                                                }}
                                                            >
                                                                <FormLabel component="legend" sx={{ px: 0.5, fontSize: 12, color: 'text.secondary' }}>
                                                                    Options
                                                                </FormLabel>
                                                                <List dense>
                                                                    {state.model.Options?.map((val, index) => (
                                                                        <ListItem
                                                                            key={`${index}-${val}`}
                                                                            secondaryAction={isEditable &&
                                                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                                                    <IconButton
                                                                                        aria-label="Move up"
                                                                                        size="small"
                                                                                        onClick={() => moveOption(index, index - 1)}
                                                                                        disabled={index === 0}
                                                                                    >
                                                                                        <ArrowUpwardIcon fontSize="small" />
                                                                                    </IconButton>
                                                                                    <IconButton
                                                                                        aria-label="Move down"
                                                                                        size="small"
                                                                                        onClick={() => moveOption(index, index + 1)}
                                                                                        disabled={index === opts.length - 1}
                                                                                    >
                                                                                        <ArrowDownwardIcon fontSize="small" />
                                                                                    </IconButton>
                                                                                    <IconButton aria-label="Delete" edge="end" onClick={() => removeOptionAt(index)}>
                                                                                        <DeleteIcon />
                                                                                    </IconButton>
                                                                                </Box>
                                                                            }
                                                                        >
                                                                            <TextField
                                                                                fullWidth
                                                                                value={val}
                                                                                disabled={!isEditable}
                                                                                onChange={(e) => updateOptionAt(index, e.target.value)}
                                                                            />
                                                                        </ListItem>
                                                                    ))}
                                                                </List>
                                                                {isEditable &&
                                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                                        <TextField
                                                                            placeholder="New value"
                                                                            value={state.newOption}
                                                                            onChange={(e) => dispatch({ type: 'NEW_OPTION_SET', value: e.target.value })}
                                                                            onKeyDown={(e) => { if (e.key === 'Enter') addOption(); }}
                                                                            fullWidth
                                                                        />
                                                                        <Button startIcon={<AddIcon />} variant="contained" onClick={addOption}>
                                                                            Add
                                                                        </Button>
                                                                    </Box>
                                                                }
                                                            </Box>
                                                        )}
                                                        {state.model.Type === PlaceholderType.float && (
                                                            <>
                                                                <TextField value={state.dpDraft}
                                                                    label="Decimal Places"
                                                                    type="number"
                                                                    disabled={!isEditable}
                                                                    onChange={(e) => dispatch({ type: 'DP_DRAFT_SET', value: e.target.value })}
                                                                    onBlur={commitDecimalPlaces}
                                                                    onKeyDown={(e) => { if (e.key === 'Enter') commitDecimalPlaces(); }} />
                                                                {state.dpError && <span style={{ color: '#d32f2f', fontSize: 12 }}>{state.dpError}</span>}
                                                            </>
                                                        )}
                                                    </Stack>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </fieldset>
                                </Paper>
                            </Stack>
                        </fieldset>
                    </>
                )}

                <div ref={itemsEndRef} />
                <Grid container spacing={2} justifyContent="flex-end" sx={{ mt: '1em' }}>
                    <Grid item xs={6}></Grid>
                    <SaveAndDeleteButtons
                        onSave={onSaveClick}
                        onDelete={handleDelete}
                        disabled={!isKeyValid || !state.hasUnsavedChanges}
                        saving={isSaving}
                    />
                </Grid>

                <Snackbar open={openAlert} autoHideDuration={6000} onClose={handleAlertClose}>
                    <Alert severity="success" sx={{ width: '100%' }}>
                        Placeholder updated successfully.
                    </Alert>
                </Snackbar>

                <ConfirmDeleteModal
                    open={openDeleteModal}
                    onClose={() => setOpenDeleteModal(false)}
                    onConfirm={confirmDelete}
                />
            </EditableContext.Provider>
        </div>
    );
};

export default PlaceholderPage;